import React, { useState, useRef } from 'react';
import {
  MdUpload, MdDownload, MdClose, MdCheckCircle, MdError,
  MdWarning, MdRefresh, MdFilePresent
} from 'react-icons/md';
import * as XLSX from 'xlsx';
import './BulkUpload.css';

/**
 * Reusable Bulk Upload Component
 *
 * @param {Object} props
 * @param {string} props.title - Modal title
 * @param {string} props.entityName - Name of the entity (e.g., "Job Requisitions", "Candidates")
 * @param {Array} props.templateColumns - Array of column definitions for the template
 *   e.g., [{ name: 'title', label: 'Job Title', required: true, type: 'string' }]
 * @param {Function} props.onUpload - Async function to handle the parsed data
 * @param {Function} props.onClose - Function to close the modal
 * @param {Object} props.dropdownData - Object with dropdown values for validation
 *   e.g., { verticals: [...], locations: [...] }
 */
function BulkUpload({
  title = 'Bulk Upload',
  entityName = 'Records',
  templateColumns = [],
  onUpload,
  onClose,
  dropdownData = {}
}) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [step, setStep] = useState('upload'); // 'upload', 'preview', 'result'

  // Parse CSV content
  const parseCSV = (content) => {
    const lines = content.split('\n').map(line => line.trim()).filter(line => line);
    if (lines.length < 2) {
      return { data: [], errors: ['File must have at least a header row and one data row'] };
    }

    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
    const data = [];
    const parseErrors = [];
    const parseWarnings = [];

    // Validate headers
    const requiredColumns = templateColumns.filter(c => c.required).map(c => c.name);
    const missingRequired = requiredColumns.filter(col =>
      !headers.some(h => h.toLowerCase() === col.toLowerCase())
    );

    if (missingRequired.length > 0) {
      parseErrors.push(`Missing required columns: ${missingRequired.join(', ')}`);
      return { data: [], errors: parseErrors };
    }

    // Parse data rows
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);

      // Skip empty/summary rows (e.g., "Total" rows at end of sheet)
      const nonEmptyCount = values.filter(v => v && v.trim()).length;
      if (nonEmptyCount < 3) continue;
      const firstCells = values.slice(0, 3).map(v => (v || '').trim().toLowerCase());
      if (firstCells.some(v => ['total', 'grand total', 'summary', 'subtotal'].includes(v))) continue;

      const row = {};
      let rowHasError = false;

      headers.forEach((header, idx) => {
        const col = templateColumns.find(c => c.name.toLowerCase() === header.toLowerCase());
        const value = values[idx]?.trim() || '';

        if (col) {
          // Validate required
          if (col.required && !value) {
            parseErrors.push(`Row ${i}: Missing required value for "${col.label}"`);
            rowHasError = true;
          }

          // Validate type
          if (value) {
            if (col.type === 'number' && isNaN(Number(value))) {
              parseErrors.push(`Row ${i}: "${col.label}" must be a number`);
              rowHasError = true;
            }
            if (col.type === 'date' && isNaN(Date.parse(value))) {
              parseErrors.push(`Row ${i}: "${col.label}" must be a valid date (YYYY-MM-DD)`);
              rowHasError = true;
            }
            if (col.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
              parseErrors.push(`Row ${i}: "${col.label}" must be a valid email`);
              rowHasError = true;
            }
            if (col.dropdown && dropdownData[col.dropdown]) {
              const validValues = dropdownData[col.dropdown].map(v =>
                (v.name || v.label || v).toLowerCase()
              );
              if (!validValues.includes(value.toLowerCase())) {
                parseWarnings.push(`Row ${i}: "${value}" may not match existing ${col.label}`);
              }
            }
          }

          row[col.name] = value;
        } else {
          row[header] = value;
        }
      });

      if (!rowHasError) {
        data.push({ ...row, _rowNum: i });
      }
    }

    return { data, errors: parseErrors, warnings: parseWarnings };
  };

  // Parse a single CSV line handling quoted values
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  // Parse Excel file using XLSX library
  const parseExcel = (arrayBuffer) => {
    try {
      const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) {
        return { data: [], errors: ['No sheets found in the Excel file'] };
      }
      const sheet = workbook.Sheets[sheetName];
      // Convert sheet to array of objects with raw headers
      const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      if (jsonData.length === 0) {
        return { data: [], errors: ['No data rows found in the Excel file'] };
      }

      const parseErrors = [];
      const parseWarnings = [];

      // Validate headers from the first row keys
      const headers = Object.keys(jsonData[0]);
      const requiredColumns = templateColumns.filter(c => c.required).map(c => c.name);
      const missingRequired = requiredColumns.filter(col =>
        !headers.some(h => h.trim().toLowerCase() === col.toLowerCase())
      );

      if (missingRequired.length > 0) {
        parseErrors.push(`Missing required columns: ${missingRequired.join(', ')}`);
        return { data: [], errors: parseErrors };
      }

      const data = [];
      jsonData.forEach((rawRow, rowIndex) => {
        // Skip summary/total rows
        const firstValues = Object.values(rawRow).slice(0, 3).map(v => String(v || '').trim().toLowerCase());
        if (firstValues.some(v => ['total', 'grand total', 'summary', 'subtotal'].includes(v))) return;

        const row = {};
        let rowHasError = false;

        templateColumns.forEach(col => {
          // Find matching header (case-insensitive)
          const matchingKey = Object.keys(rawRow).find(k => k.trim().toLowerCase() === col.name.toLowerCase());
          let value = matchingKey !== undefined ? String(rawRow[matchingKey] ?? '').trim() : '';

          // Handle date objects from Excel
          if (matchingKey !== undefined && rawRow[matchingKey] instanceof Date) {
            const d = rawRow[matchingKey];
            value = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
          }

          if (col.required && !value) {
            parseErrors.push(`Row ${rowIndex + 2}: Missing required value for "${col.label}"`);
            rowHasError = true;
          }

          if (value) {
            if (col.type === 'number' && isNaN(Number(value))) {
              parseErrors.push(`Row ${rowIndex + 2}: "${col.label}" must be a number`);
              rowHasError = true;
            }
          }

          row[col.name] = value;
        });

        // Also capture any extra columns not in templateColumns
        Object.keys(rawRow).forEach(key => {
          const trimmedKey = key.trim();
          if (!templateColumns.find(c => c.name.toLowerCase() === trimmedKey.toLowerCase())) {
            row[trimmedKey] = String(rawRow[key] ?? '').trim();
          }
        });

        if (!rowHasError) {
          data.push({ ...row, _rowNum: rowIndex + 2 });
        }
      });

      return { data, errors: parseErrors, warnings: parseWarnings };
    } catch (e) {
      return { data: [], errors: [`Failed to parse Excel file: ${e.message}`] };
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const fileName = selectedFile.name.toLowerCase();
    const isCSV = fileName.endsWith('.csv');
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

    if (!isCSV && !isExcel) {
      setErrors(['Please upload a CSV (.csv) or Excel (.xlsx, .xls) file']);
      return;
    }

    setFile(selectedFile);
    setErrors([]);
    setWarnings([]);
    setUploadResult(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      let result;
      if (isExcel) {
        result = parseExcel(event.target.result);
      } else {
        result = parseCSV(event.target.result);
      }

      const { data, errors: parseErrors, warnings: parseWarnings } = result;
      setParsedData(data);
      setErrors(parseErrors || []);
      setWarnings(parseWarnings || []);

      if (data.length > 0 && parseErrors.length === 0) {
        setStep('preview');
      }
    };

    if (isExcel) {
      reader.readAsArrayBuffer(selectedFile);
    } else {
      reader.readAsText(selectedFile);
    }
  };

  // Download template
  const downloadTemplate = () => {
    const headers = templateColumns.map(c => c.name).join(',');
    const sampleRow = templateColumns.map(c => {
      if (c.example) return c.example;
      if (c.type === 'number') return '0';
      if (c.type === 'date') return '2025-01-01';
      return '';
    }).join(',');

    const csv = `${headers}\n${sampleRow}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${entityName.toLowerCase().replace(/\s+/g, '_')}_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Handle upload
  const handleUpload = async () => {
    if (parsedData.length === 0) return;

    setIsUploading(true);
    try {
      const result = await onUpload(parsedData, file);
      const resData = result?.data || {};
      const backendErrors = resData.errors || [];
      const created = resData.jrCreated || resData.candidatesCreated || 0;
      const updated = resData.jrUpdated || resData.candidatesUpdated || 0;
      const totalProcessed = created + updated;

      setUploadResult({
        success: true,
        message: result?.message || `Successfully uploaded ${parsedData.length} ${entityName}`,
        created,
        updated,
        totalProcessed,
        failedErrors: backendErrors,
        partialSuccess: result?.partialSuccess || false
      });
      setStep('result');
    } catch (error) {
      const errData = error.response?.data?.data || {};
      const backendErrors = errData.errors || [];
      setUploadResult({
        success: false,
        message: error.response?.data?.message || error.message || 'Upload failed',
        failedErrors: backendErrors,
        created: 0,
        updated: 0,
        totalProcessed: 0
      });
      setStep('result');
    } finally {
      setIsUploading(false);
    }
  };

  // Reset to initial state
  const handleReset = () => {
    setFile(null);
    setParsedData([]);
    setErrors([]);
    setWarnings([]);
    setUploadResult(null);
    setStep('upload');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content bulk-upload-modal">
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>
            <MdClose />
          </button>
        </div>

        <div className="bulk-upload-content">
          {step === 'upload' && (
            <>
              <div className="upload-instructions">
                <h4>Upload {entityName}</h4>
                <p>Upload a CSV or Excel file with {entityName.toLowerCase()} data. Download the template to see the required format.</p>

                <div className="template-section">
                  <button className="btn-template" onClick={downloadTemplate}>
                    <MdDownload />
                    Download Template
                  </button>
                </div>

                <div className="column-info">
                  <h5>Required Columns:</h5>
                  <ul>
                    {templateColumns.filter(c => c.required).map(c => (
                      <li key={c.name}>
                        <strong>{c.label}</strong>
                        {c.type && <span className="col-type"> ({c.type})</span>}
                      </li>
                    ))}
                  </ul>

                  {templateColumns.filter(c => !c.required).length > 0 && (
                    <>
                      <h5>Optional Columns:</h5>
                      <ul>
                        {templateColumns.filter(c => !c.required).map(c => (
                          <li key={c.name}>
                            {c.label}
                            {c.type && <span className="col-type"> ({c.type})</span>}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>

              <div
                className="drop-zone"
                onClick={() => fileInputRef.current?.click()}
              >
                <MdUpload className="drop-icon" />
                <p>Click to select or drag and drop a CSV or Excel file</p>
                <span className="file-types">.csv, .xlsx, .xls files supported</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </div>

              {errors.length > 0 && (
                <div className="error-list">
                  <h5><MdError /> Errors</h5>
                  <ul>
                    {errors.slice(0, 10).map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                    {errors.length > 10 && (
                      <li>...and {errors.length - 10} more errors</li>
                    )}
                  </ul>
                </div>
              )}
            </>
          )}

          {step === 'preview' && (
            <>
              <div className="preview-header">
                <div className="file-info">
                  <MdFilePresent />
                  <span>{file?.name}</span>
                  <span className="record-count">{parsedData.length} records</span>
                </div>
                <button className="btn-reset" onClick={handleReset}>
                  <MdRefresh /> Choose Different File
                </button>
              </div>

              {warnings.length > 0 && (
                <div className="warning-list">
                  <h5><MdWarning /> Warnings</h5>
                  <ul>
                    {warnings.slice(0, 5).map((warn, idx) => (
                      <li key={idx}>{warn}</li>
                    ))}
                    {warnings.length > 5 && (
                      <li>...and {warnings.length - 5} more warnings</li>
                    )}
                  </ul>
                </div>
              )}

              <div className="preview-table-container">
                <table className="preview-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      {templateColumns.slice(0, 6).map(col => (
                        <th key={col.name}>{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.slice(0, 5).map((row, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        {templateColumns.slice(0, 6).map(col => (
                          <td key={col.name}>{row[col.name] || '-'}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsedData.length > 5 && (
                  <p className="more-records">...and {parsedData.length - 5} more records</p>
                )}
              </div>

              <div className="upload-actions">
                <button className="btn-cancel" onClick={handleReset}>
                  Cancel
                </button>
                <button
                  className="btn-upload"
                  onClick={handleUpload}
                  disabled={isUploading}
                >
                  {isUploading ? 'Uploading...' : `Upload ${parsedData.length} Records`}
                </button>
              </div>
            </>
          )}

          {step === 'result' && (
            <div className="result-container">
              {uploadResult?.success ? (
                <>
                  <div className="result-icon success">
                    <MdCheckCircle />
                  </div>
                  <h4>{uploadResult.failedErrors?.length > 0 ? 'Upload Partially Successful' : 'Upload Successful!'}</h4>
                  <div className="result-summary">
                    <div className="summary-row">
                      <span className="summary-label">Created:</span>
                      <span className="summary-value success-text">{uploadResult.created}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Updated:</span>
                      <span className="summary-value success-text">{uploadResult.updated}</span>
                    </div>
                    {uploadResult.failedErrors?.length > 0 && (
                      <div className="summary-row">
                        <span className="summary-label">Failed:</span>
                        <span className="summary-value error-text">{uploadResult.failedErrors.length}</span>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="result-icon error">
                    <MdError />
                  </div>
                  <h4>Upload Failed</h4>
                  <p>{uploadResult?.message}</p>
                </>
              )}

              {/* Show failed rows with reasons */}
              {uploadResult?.failedErrors?.length > 0 && (
                <div className="failed-rows-section">
                  <h5><MdWarning style={{ verticalAlign: 'middle', marginRight: '6px', color: '#dc3545' }} />
                    Failed Records ({uploadResult.failedErrors.length})
                  </h5>
                  <div className="failed-rows-table-container">
                    <table className="failed-rows-table">
                      <thead>
                        <tr>
                          <th style={{ width: '50px' }}>#</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadResult.failedErrors.map((err, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td>{err}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="result-actions">
                <button className="btn-reset" onClick={handleReset}>
                  Upload More
                </button>
                <button className="btn-done" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BulkUpload;
